<template>
	<div>
		<Breadcrumbs main="App" title="Draw" />
		<div class="container-fluid">
			<div class="row">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<div>
								<h5>Draw Management</h5>
								<span>Create, edit, and delete draw entries.</span>
							</div>
							<div class="d-flex gap-2">
								<button class="btn btn-outline-primary" type="button" @click="fetchDraws" :disabled="loading">
									Refresh
								</button>
								<button
									v-if="canCreate"
									class="btn btn-primary"
									type="button"
									@click="openCreate"
								>
									New Draw
								</button>
							</div>
						</div>

						<div class="card-body">
							<div v-if="errorMessage" class="alert alert-danger" role="alert">
								{{ errorMessage }}
							</div>
							<div v-if="successMessage" class="alert alert-success" role="alert">
								{{ successMessage }}
							</div>

							<div class="mb-3 row align-items-center">
								<label for="draw-search" class="col-sm-2 col-form-label">Search</label>
								<div class="col-sm-10">
									<input
										id="draw-search"
										v-model="searchTerm"
										class="form-control"
										type="text"
										placeholder="Filter by draw name"
									/>
								</div>
							</div>

							<div class="table-responsive">
								<table class="table table-striped table-bordered">
									<thead>
										<tr>
											<th>Name</th>
											<th>Draw Time</th>
											<th>Status</th>
											<th>Success</th>
											<th>Created At</th>
											<th>Updated At</th>
											<th class="text-center">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr v-if="loading">
											<td colspan="7" class="text-center">Loading...</td>
										</tr>
										<tr v-else-if="!paginatedDraws.length">
											<td colspan="7" class="text-center">No draw data found.</td>
										</tr>
										<tr v-for="draw in paginatedDraws" :key="draw.id">
											<td>{{ draw.drawName }}</td>
											<td>{{ formatDate(draw.drawTime) }}</td>
											<td>
												<span :class="draw.isOpen ? 'badge badge-success' : 'badge badge-secondary'">
													{{ draw.isOpen ? 'Open' : 'Closed' }}
												</span>
											</td>
											<td>
												<span :class="draw.isSuccess ? 'badge badge-primary' : 'badge badge-light'">
													{{ draw.isSuccess ? 'Yes' : 'No' }}
												</span>
											</td>
											<td>{{ formatDate(draw.createdAt) }}</td>
											<td>{{ formatDate(draw.updatedAt) }}</td>
											<td class="text-center">
												<div class="btn-group" role="group" aria-label="Draw actions">
													<button
														v-if="canUpdate"
														class="btn btn-sm btn-warning"
														type="button"
														@click="openEdit(draw)"
													>
														Edit
													</button>
													<button
														v-if="canDelete"
														class="btn btn-sm btn-danger"
														type="button"
														@click="deleteDraw(draw)"
													>
														Delete
													</button>
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div v-if="computedTotalPages > 1" class="d-flex justify-content-between align-items-center mt-2">
								<small class="text-muted">
									Page {{ pagination.pageNumber }} of {{ computedTotalPages }} ({{ computedTotalItems }} records)
								</small>
								<div class="d-flex gap-2">
									<button
										class="btn btn-sm btn-outline-secondary"
										:disabled="pagination.pageNumber <= 1"
										@click="goToPage(pagination.pageNumber - 1)"
									>
										&laquo; Prev
									</button>
									<button
										class="btn btn-sm btn-outline-secondary"
										:disabled="pagination.pageNumber >= computedTotalPages"
										@click="goToPage(pagination.pageNumber + 1)"
									>
										Next &raquo;
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="row" v-if="showForm && (canCreate || canUpdate)">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<h5 class="mb-0">{{ isEditing ? 'Edit Draw' : 'Create Draw' }}</h5>
							<button class="btn btn-outline-secondary btn-sm" type="button" @click="closeForm">Close</button>
						</div>
						<div class="card-body">
							<form @submit.prevent="saveDraw">
								<div class="row g-3">
									<div class="col-md-6">
										<label class="form-label" for="draw-name">Draw Name</label>
										<input id="draw-name" v-model.trim="form.drawName" type="text" class="form-control" required />
									</div>

									<div class="col-md-6">
										<label class="form-label" for="draw-time">Draw Time</label>
										<input id="draw-time" v-model="form.drawTime" type="datetime-local" class="form-control" required />
									</div>

									<div class="col-md-3">
										<div class="form-check mt-2">
											<input id="draw-open" v-model="form.isOpen" class="form-check-input" type="checkbox" />
											<label for="draw-open" class="form-check-label">Is Open</label>
										</div>
									</div>

									<div class="col-md-3">
										<div class="form-check mt-2">
											<input id="draw-success" v-model="form.isSuccess" class="form-check-input" type="checkbox" />
											<label for="draw-success" class="form-check-label">Is Success</label>
										</div>
									</div>

									<div class="col-12 d-flex gap-2">
										<button class="btn btn-primary" type="submit" :disabled="submitting">
											{{ submitting ? 'Saving...' : (isEditing ? 'Update Draw' : 'Create Draw') }}
										</button>
										<button class="btn btn-outline-secondary" type="button" @click="closeForm">Cancel</button>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { API_BASE } from '../../config/api';

export default {
	name: 'DrawList',
	data() {
		return {
			baseUrl: `${API_BASE}/api/Draw`,
			draws: [],
			loading: false,
			submitting: false,
			showForm: false,
			isEditing: false,
			searchTerm: '',
			errorMessage: '',
			successMessage: '',
			pagination: {
				pageNumber: 1,
				pageSize: 20,
				totalItems: 0,
				totalPages: 1,
			},
			form: {
				id: '',
				drawName: '',
				drawTime: '',
				isOpen: false,
				isSuccess: false,
			},
		};
	},
	computed: {
		permissions() {
			return this.$store?.state?.auth?.permissions || [];
		},
		canCreate() {
			return this.permissions.includes('draw:create');
		},
		canUpdate() {
			return this.permissions.includes('draw:update');
		},
		canDelete() {
			return this.permissions.includes('draw:delete');
		},
		canRead() {
			return this.permissions.includes('draw:read');
		},
		sortedDraws() {
			return [...this.draws].sort((a, b) => {
				const aTime = new Date(a.drawTime || 0).getTime();
				const bTime = new Date(b.drawTime || 0).getTime();
				return bTime - aTime;
			});
		},
		filteredDraws() {
			const query = this.searchTerm.trim().toLowerCase();
			if (!query) return this.sortedDraws;
			return this.sortedDraws.filter(d => (d.drawName || '').toLowerCase().includes(query));
		},
		paginatedDraws() {
			const start = (this.pagination.pageNumber - 1) * this.pagination.pageSize;
			const end = start + this.pagination.pageSize;
			return this.filteredDraws.slice(start, end);
		},
		computedTotalItems() {
			return this.filteredDraws.length;
		},
		computedTotalPages() {
			if (this.computedTotalItems === 0) return 1;
			return Math.ceil(this.computedTotalItems / this.pagination.pageSize);
		},
	},
	watch: {
		searchTerm() {
			this.pagination.pageNumber = 1;
		},
	},
	mounted() {
		this.$store.dispatch('auth/loadPermissions');
		if (this.canRead) {
			this.fetchDraws();
		} else {
			this.errorMessage = 'You do not have read permission.';
		}
	},
	methods: {
		authHeaders(includeJson = false) {
			const token = localStorage.getItem('token');
			const headers = {};
			if (includeJson) headers['Content-Type'] = 'application/json';
			if (token) headers.Authorization = `Bearer ${token}`;
			return headers;
		},
		clearMessages() {
			this.errorMessage = '';
			this.successMessage = '';
		},
		normalizeDateTimeLocal(value) {
			if (!value) return '';
			const date = new Date(value);
			if (Number.isNaN(date.getTime())) return '';
			const pad = (n) => String(n).padStart(2, '0');
			return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
		},
		formatDate(value) {
			if (!value) return '-';
			const date = new Date(value);
			if (Number.isNaN(date.getTime())) return '-';
			const day = String(date.getDate()).padStart(2, '0');
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const year = date.getFullYear();
			return `${day}/${month}/${year}`;
		},
		resetForm() {
			this.form = {
				id: '',
				drawName: '',
				drawTime: '',
				isOpen: false,
				isSuccess: false,
			};
		},
		openCreate() {
			this.clearMessages();
			this.isEditing = false;
			this.resetForm();
			this.showForm = true;
		},
		openEdit(draw) {
			this.clearMessages();
			this.isEditing = true;
			this.form = {
				id: draw.id,
				drawName: draw.drawName || '',
				drawTime: this.normalizeDateTimeLocal(draw.drawTime),
				isOpen: !!draw.isOpen,
				isSuccess: !!draw.isSuccess,
			};
			this.showForm = true;
		},
		closeForm() {
			this.showForm = false;
			this.isEditing = false;
			this.resetForm();
		},
		async fetchDraws() {
			this.clearMessages();
			this.loading = true;
			try {
				const response = await fetch(this.baseUrl, {
					method: 'GET',
					headers: this.authHeaders(),
				});
				if (!response.ok) {
					const text = await response.text();
					throw new Error(text || `Failed to load draws (${response.status})`);
				}
				const data = await response.json();
				this.draws = Array.isArray(data) ? data : (data.items || data.data || []);
				this.pagination.pageNumber = 1;
				this.pagination.totalItems = this.draws.length;
				this.pagination.totalPages = Math.max(1, Math.ceil(this.draws.length / this.pagination.pageSize));
			} catch (error) {
				this.errorMessage = error.message || 'Failed to load draw data.';
			} finally {
				this.loading = false;
			}
		},
		goToPage(page) {
			if (page < 1 || page > this.computedTotalPages) return;
			this.pagination.pageNumber = page;
		},
		async saveDraw() {
			this.clearMessages();
			this.submitting = true;
			try {
				const payload = {
					id: this.isEditing ? this.form.id : undefined,
					drawName: this.form.drawName,
					drawTime: this.form.drawTime,
					isOpen: !!this.form.isOpen,
					isSuccess: !!this.form.isSuccess,
				};

				const url = this.isEditing ? `${this.baseUrl}/${this.form.id}` : this.baseUrl;
				const method = this.isEditing ? 'PUT' : 'POST';

				const response = await fetch(url, {
					method,
					headers: this.authHeaders(true),
					body: JSON.stringify(payload),
				});

				if (!response.ok) {
					const text = await response.text();
					throw new Error(text || `Failed to ${this.isEditing ? 'update' : 'create'} draw (${response.status})`);
				}

				this.successMessage = this.isEditing ? 'Draw updated successfully.' : 'Draw created successfully.';
				this.closeForm();
				await this.fetchDraws();
			} catch (error) {
				this.errorMessage = error.message || 'Save operation failed.';
			} finally {
				this.submitting = false;
			}
		},
		async deleteDraw(draw) {
			this.clearMessages();
			const confirmed = window.confirm(`Delete draw "${draw.drawName}"?`);
			if (!confirmed) return;

			try {
				const response = await fetch(`${this.baseUrl}/${draw.id}`, {
					method: 'DELETE',
					headers: this.authHeaders(),
				});

				if (!response.ok) {
					const text = await response.text();
					throw new Error(text || `Failed to delete draw (${response.status})`);
				}

				this.successMessage = 'Draw deleted successfully.';
				await this.fetchDraws();
			} catch (error) {
				this.errorMessage = error.message || 'Delete operation failed.';
			}
		},
	},
};
</script>
